import React from 'react';

import { initUFO } from '@wac-web/analytics/react-ufo/initUFO';
import { getAnalyticsWebClient } from '@wac-web/analytics/wac-analytics/get-analytics-web-client';
import { initializeFeatureGatesClient } from '@wac-web/feature-gates/initialization';
import {
	ServerSideRequestStatus,
	UserProfile,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import {
	extractOptanonConsentCookie,
	isPerformanceOnetrustEnabled,
} from '@wac-web/utils/onetrust-provider';
import { SentryIntlProvider } from '@wac-web/utils/sentry-intl';
import { UserLocale, LocationData } from '@wac-web/utils/useGeoCurrency';
import { processExperimentAndFeatureGateOverrides } from '@wac-web/utils/wac-browser-utility';
import { parse } from 'cookie';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { v4 as uuidv4 } from 'uuid';

import { parseRoute } from './fastshift-portal-routes';
import { Layout } from './layout';
import { PageWrapper } from './page-wrapper';

window.BUILD_KEY = process.env['VERSION'];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
	identifiers: {
		analyticsAnonymousId: uuidv4(),
	},
};

declare global {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore duplicate
	type FeatureFlagType = 'boolean' | 'string' | 'number';

	interface Window {
		IS_STATIC?: boolean;
		BUILD_KEY?: string;
		optanonConsentCookie?: string;
		entryName?: string;
		SSR_languagePack: Record<string, string>;
		userProfile?: UserProfile;
		userProfileStatus?: ServerSideRequestStatus;
		featureFlags: Record<string, FeatureFlagType>;
		countryCode?: UserLocale;
		regionIsoCode?: string;
		AtlOneTrustWrapper?: { updatePolicyLinks: () => void };
		track?: string;
		ssrAnonId?: string;
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const init = (Component: React.ComponentType<any>) => {
	const isStatic = typeof window !== 'undefined' && window.IS_STATIC;
	const analyticsWebClient = getAnalyticsWebClient();
	const { locale, path } = parseRoute(window.location);
	// Get location data for experiments and feature gates from server-provided data
	let locationData: LocationData | undefined;

	// Use server-provided data (no client-side API calls)

	if (typeof window !== 'undefined') {
		locationData = {
			countryIsoCode: window.countryCode || '',
			regionIsoCode: window.regionIsoCode || '',
		};
	}

	const isPerfOnetrustEnabled = isPerformanceOnetrustEnabled(
		typeof window !== 'undefined'
			? window.optanonConsentCookie
			: extractOptanonConsentCookie(document.cookie),
		locationData,
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	isPerfOnetrustEnabled && initUFO(analyticsWebClient, path);
	const parsedClientCookies = document.cookie ? parse(document.cookie) : {};
	const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : '';

	const { finalExperimentOverrides, finalFeatureGateOverrides } =
		processExperimentAndFeatureGateOverrides(parsedClientCookies, userAgent, isPerfOnetrustEnabled);

	initializeFeatureGatesClient(window.featureGateBootstrapValues, {
		overrideValues: {
			experimentOverrides: finalExperimentOverrides,
			featureGateOverrides: finalFeatureGateOverrides,
		},
		// if performance cookies are disabled, we will skip exposures
		localMode: !isPerfOnetrustEnabled,
	}).then(() => {
		if (isStatic) {
			const element = document.getElementById('fastshift-portal');
			if (element) {
				const root = createRoot(element);
				root.render(
					<SentryIntlProvider
						locale={locale}
						messages={window.SSR_languagePack}
						cookies={document.cookie}
					>
						<PageWrapper
							// @ts-expect-error type
							analyticsWebClient={analyticsWebClient}
							pureClientMode={true}
							location={window.location}
							userProfile={window.userProfile}
							userProfileStatus={window.userProfileStatus}
							countryCode={window.countryCode}
						>
							{Component}
						</PageWrapper>
					</SentryIntlProvider>,
				);
			}
		} else {
			const element = document.getElementById('fastshift-portal-root');
			if (element) {
				hydrateRoot(
					element,
					<SentryIntlProvider
						locale={locale}
						messages={window.SSR_languagePack}
						cookies={document.cookie}
					>
						<Layout
							// @ts-expect-error type
							analyticsWebClient={analyticsWebClient}
							location={window.location}
							userProfile={window.userProfile}
							userProfileStatus={window.userProfileStatus}
							countryCode={window.countryCode}
						>
							{Component}
						</Layout>
					</SentryIntlProvider>,
					{
						onRecoverableError: (error, errorInfo) => {
							// add an attribute if in CI
							if (process.env['CI'] === 'true') {
								document
									.getElementById('fastshift-portal-root')
									?.setAttribute('hydration-error', 'true');
							}
							if (process.env['MODE'] === 'dev') {
								console.log(`Recoverable Error (including Hydration): ${error}`, errorInfo);
							}
						},
					},
				);
			}
		}
	});
};
